/**
 * MassInputGrid — reusable Excel-like input grid (style Goods Allocation ›
 * Mass Upload): dán trực tiếp từ Excel (onPaste nở dòng), Ctrl+V paste new rows,
 * checkbox chọn dòng + Copy/Delete/Reset, cột status ✓/✗ cross-check realtime +
 * tô đỏ field lỗi + Alert panel. Dùng chung cho các dialog batch (Invoice /
 * Pay Outright / Pay PTBF).
 *
 * Parent giữ `rows` (dạng { id, data }) + `selected` state và truyền:
 *  - `columns`: định nghĩa cột (text | number | combo, có options cho combo).
 *  - `makeEmptyRow` / `isRowFilled`: factory + cách nhận biết dòng có dữ liệu
 *    (dialog-specific — vd Pay Outright bỏ qua payment_type khi xét rỗng).
 *  - `verdicts`: kết quả cross-check per-row (parent tự tính, mirror rule BE).
 *
 * KHÔNG force 2-decimal khi blur (giữ nguyên precision USD/price của Pay dialog);
 * paste chỉ bỏ dấu phẩy nghìn.
 */
import { useCallback, useEffect } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import AddIcon from '@mui/icons-material/Add'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  Popper,
  type PopperProps,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import type { useSnackbar } from '@/hooks/useSnackbar'
import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

// ------------------------------------------------------------------ styling --
const MASS_FIELD_H = 32
const massCellSx = { px: 0.5, py: 0.25 } as const
const massInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    height: MASS_FIELD_H,
    minHeight: MASS_FIELD_H,
    fontSize: '0.72rem',
    py: 0,
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input': { py: 0, fontSize: '0.72rem' },
  '& .MuiAutocomplete-input': { py: '0 !important', fontSize: '0.72rem' },
}
const massHeaderSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  lineHeight: 1.2,
  py: 0.5,
  px: 0.75,
  bgcolor: '#fafafa',
}
const toolbarBtnSx: SxProps<Theme> = {
  fontSize: '0.7rem',
  py: 0.25,
  px: 0.875,
  minHeight: 26,
  textTransform: 'none',
}

// ------------------------------------------------------------------- types --
export type MassColumnKind = 'text' | 'number' | 'date' | 'combo'
export interface MassComboOption {
  value: string
  label: string
}
export interface MassColumn {
  key: string
  label: string
  kind: MassColumnKind
  width: number
  required?: boolean
  /** combo: danh sách option (value = mã lưu, label = tên hiển thị). */
  options?: MassComboOption[]
  /** Gợi ý giá trị (freeSolo) — dropdown suggest nhưng vẫn cho gõ tự do (vd % Pay). */
  suggest?: string[]
  /** Cộng tổng ở dòng Total dưới bảng. */
  sum?: boolean
}
export interface MassRow {
  id: number
  data: Record<string, string>
}
export interface MassVerdict {
  ok: boolean
  badFields: Set<string>
  messages: string[]
}

// ----------------------------------------------------------------- helpers --
/** Parse TSV từ clipboard → ma trận string[][]. */
function parseMatrix(text: string): string[][] {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n$/, '')
    .split('\n')
    .map((line) => line.split('\t'))
}

function buildIsoDate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  if (year < 1900 || year > 2999) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Normalize ô ngày dán từ Excel → YYYY-MM-DD (ambiguous → D-M-Y kiểu VN). */
function parsePastedDate(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) return buildIsoDate(+iso[1], +iso[2], +iso[3]) ?? buildIsoDate(+iso[1], +iso[3], +iso[2]) ?? ''
  const sep = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/)
  if (sep) {
    const p1 = +sep[1]
    const p2 = +sep[2]
    const year = sep[3].length === 2 ? 2000 + +sep[3] : +sep[3]
    let day: number, month: number
    if (p1 > 12 && p2 <= 12) [day, month] = [p1, p2]
    else if (p2 > 12 && p1 <= 12) [day, month] = [p2, p1]
    else [day, month] = [p1, p2]
    return buildIsoDate(year, month, day) ?? ''
  }
  const t = new Date(s)
  if (!Number.isNaN(t.getTime())) return buildIsoDate(t.getFullYear(), t.getMonth() + 1, t.getDate()) ?? ''
  return ''
}

/** Chỉ feed YYYY-MM-DD hợp lệ cho <input type="date">. */
function safeDateValue(v: string): string {
  return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : ''
}

/** Bỏ dấu phẩy nghìn, giữ nguyên số (KHÔNG force decimals → không mất precision). */
function stripThousands(v: string): string {
  const s = v.replace(/,/g, '').trim()
  if (!s) return ''
  return Number.isFinite(Number(s)) ? s : ''
}

/** Parse ô số (bỏ phẩy nghìn + "%") → number (0 nếu rỗng/không hợp lệ) cho dòng Total. */
function numFromStr(v: string): number {
  const n = Number(String(v ?? '').replace(/[,%]/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

function fmtSum(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/** Header row detect — ≥2 ô đầu khớp tên cột (Copy round-trip drop header). */
function isHeaderRow(cells: string[], labels: string[]): boolean {
  let matches = 0
  cells.forEach((c, i) => {
    if (i < labels.length && c.trim().toLowerCase() === labels[i]) matches += 1
  })
  return matches >= 2
}

// ------------------------------------------------------------- clipboard --
function copyViaSelection(html: string): boolean {
  const div = document.createElement('div')
  div.contentEditable = 'true'
  div.innerHTML = html
  div.style.position = 'fixed'
  div.style.top = '-9999px'
  div.style.left = '-9999px'
  div.style.opacity = '0'
  document.body.appendChild(div)
  const selection = window.getSelection()
  if (!selection) {
    document.body.removeChild(div)
    return false
  }
  const savedRanges: Range[] = []
  for (let i = 0; i < selection.rangeCount; i++) savedRanges.push(selection.getRangeAt(i).cloneRange())
  const range = document.createRange()
  range.selectNodeContents(div)
  selection.removeAllRanges()
  selection.addRange(range)
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  selection.removeAllRanges()
  savedRanges.forEach((r) => selection.addRange(r))
  document.body.removeChild(div)
  return ok
}

function copyViaTextarea(text: string): boolean {
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.top = '-9999px'
  document.body.appendChild(ta)
  const prevActive = document.activeElement as HTMLElement | null
  ta.focus()
  ta.select()
  let ok = false
  try {
    ok = document.execCommand('copy')
  } catch {
    ok = false
  }
  document.body.removeChild(ta)
  prevActive?.focus?.()
  return ok
}

// --------------------------------------------------------------- ComboCell --
function WideComboPopper(props: PopperProps) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...props.style, width: 'fit-content', minWidth: 200, maxWidth: 480 }}
    />
  )
}

/** Cell combobox — Autocomplete flat: field hiện code, dropdown "<CODE> — <NAME>".
 *  Value đã có (dán) được inject vào options để không mất khi chưa khớp list. */
function ComboCell({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string
  options: MassComboOption[]
  onChange: (v: string) => void
  disabled: boolean
}) {
  const sel = options.find((o) => o.value === value) ?? (value ? { value, label: value } : undefined)
  const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size="small"
      fullWidth
      options={merged}
      value={sel}
      disabled={disabled}
      disableClearable
      onChange={(_e, v) => onChange(v?.value ?? '')}
      PopperComponent={WideComboPopper}
      getOptionLabel={(o) => (o ? String(o.value ?? '') : '')}
      renderOption={(props, o, state) => {
        const code = String(o.value ?? '')
        const lab = String(o.label ?? '')
        const text = !lab || lab === code ? code : `${code} — ${lab}`
        const { key, ...rest } = props as typeof props & { key?: React.Key }
        return (
          <li {...rest} key={`${code}__${lab}__${state.index}`} style={{ whiteSpace: 'nowrap' }}>
            {text}
          </li>
        )
      }}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      // Search theo cả label hiện trong dropdown — getOptionLabel chỉ trả code
      // nên default filter KHÔNG match được tên (anti-pattern #55 /convert-form).
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase()
        if (!q) return opts
        return opts.filter((o) =>
          `${o.value ?? ''} ${o.label ?? ''}`.toLowerCase().includes(q))
      }}
      renderInput={(p) => <TextField {...p} sx={massInputSx} />}
    />
  )
}

/** Cell freeSolo — suggest 1 list giá trị nhưng vẫn cho gõ tự do (vd % Pay). */
function SuggestCell({
  value,
  options,
  numeric,
  onChange,
  disabled,
}: {
  value: string
  options: string[]
  numeric: boolean
  onChange: (v: string) => void
  disabled: boolean
}) {
  return (
    <Autocomplete
      size="small"
      fullWidth
      freeSolo
      disableClearable
      options={options}
      inputValue={value}
      onInputChange={(_e, v) => onChange(v)}
      PopperComponent={WideComboPopper}
      renderInput={(p) => (
        <TextField
          {...p}
          sx={massInputSx}
          inputProps={{
            ...p.inputProps,
            ...(numeric ? { style: { textAlign: 'right' }, inputMode: 'decimal' } : {}),
          }}
        />
      )}
    />
  )
}

// --------------------------------------------------------------- component --
export interface MassInputGridProps {
  open: boolean
  columns: MassColumn[]
  rows: MassRow[]
  setRows: Dispatch<SetStateAction<MassRow[]>>
  selected: Set<number>
  setSelected: Dispatch<SetStateAction<Set<number>>>
  makeEmptyRow: () => MassRow
  isRowFilled: (data: Record<string, string>) => boolean
  /** Cross-check per-row (parent tính, mirror rule BE) — dài bằng `rows`. */
  verdicts: MassVerdict[]
  filledCount: number
  disabled?: boolean
  /** Text summary khi chưa nhập dòng nào (filledCount === 0). */
  emptyStateText?: string
  snackbar: ReturnType<typeof useSnackbar>
  /** Nhãn dòng cho message (vd "invoice" / "payment"). Default "row". */
  rowNoun?: string
  /** Hiện nút Copy (Copy all / Copy N). Default true. */
  showCopy?: boolean
}

export default function MassInputGrid({
  open,
  columns,
  rows,
  setRows,
  selected,
  setSelected,
  makeEmptyRow,
  isRowFilled,
  verdicts,
  filledCount,
  disabled = false,
  emptyStateText = 'Enter at least one row to check',
  snackbar,
  rowNoun = 'row',
  showCopy = true,
}: MassInputGridProps) {
  const headerLabels = columns.map((c) => c.label.toLowerCase())
  const allSelected = rows.length > 0 && selected.size === rows.length
  const failCount = verdicts.filter((v) => !v.ok).length
  const cellBg = (index: number, key: string): string | undefined => {
    const v = verdicts[index]
    return v && !v.ok && v.badFields.has(key) ? 'rgba(211,47,47,0.14)' : undefined
  }
  const hasSum = columns.some((c) => c.sum)
  const sumTotals: Record<string, number> = {}
  if (hasSum) {
    for (const c of columns) {
      if (c.sum) sumTotals[c.key] = rows.reduce((acc, r) => acc + numFromStr(r.data[c.key] ?? ''), 0)
    }
  }

  /** Chuẩn hoá 1 ô dán theo loại cột. */
  const coerceCell = useCallback((col: MassColumn, raw: string): string => {
    const v = (raw ?? '').trim()
    if (!v) return ''
    if (col.kind === 'combo') {
      // Khớp option để chuẩn hoá hoa/thường; CHƯA khớp (options load async theo
      // contract / value ngoài list) → GIỮ raw để copy→paste không mất Truck ID /
      // Bank Name (ComboCell tự merge hiển thị; hợp lệ do cross-check kiểm).
      const hit = (col.options ?? []).find((o) => String(o.value).toLowerCase() === v.toLowerCase())
      return hit ? String(hit.value) : v
    }
    // freeSolo suggest (vd % Pay "80%") — giữ raw, KHÔNG strip số (tránh "80%"→'').
    if (col.suggest) return v
    if (col.kind === 'date') return parsePastedDate(v)
    if (col.kind === 'number') return stripThousands(v)
    return v
  }, [])

  const setCell = (index: number, key: string, value: string) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, data: { ...r.data, [key]: value } } : r)))

  const applyMatrix = (prev: MassRow[], startRow: number, startCol: number, matrix: string[][]) => {
    const next = prev.map((r) => ({ id: r.id, data: { ...r.data } }))
    while (next.length < startRow + matrix.length) next.push(makeEmptyRow())
    matrix.forEach((cells, r) => {
      const target = next[startRow + r]
      cells.forEach((raw, c) => {
        const fi = startCol + c
        if (fi >= columns.length) return
        target.data[columns[fi].key] = coerceCell(columns[fi], raw)
      })
    })
    return next
  }

  const handlePaste = (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    if (!text) return
    if (!text.includes('\t') && !text.includes('\n')) return
    e.preventDefault()
    e.stopPropagation()
    let matrix = parseMatrix(text)
    // Bỏ dòng header nếu dán block copy từ chính grid (có row tên cột) — tránh
    // "Payment Type"/"Truck ID"… rơi vào ô data làm cross-check fail.
    if (matrix.length > 1 && isHeaderRow(matrix[0], headerLabels)) matrix = matrix.slice(1)
    if (!matrix.length) return
    setRows((prev) => applyMatrix(prev, rowIdx, colIdx, matrix))
  }

  const appendRowsFromText = useCallback(
    (text: string): number => {
      const matrix = parseMatrix(text)
      if (!matrix.length) return 0
      const dataRows = matrix.length > 1 && isHeaderRow(matrix[0], headerLabels) ? matrix.slice(1) : matrix
      if (!dataRows.length) return 0
      const built = dataRows.map((cells) => {
        const row = makeEmptyRow()
        cells.forEach((raw, ci) => {
          if (ci >= columns.length) return
          row.data[columns[ci].key] = coerceCell(columns[ci], raw)
        })
        return row
      })
      setRows((prev) => {
        const pristine = prev.length === 1 && !isRowFilled(prev[0].data)
        return pristine ? built : [...prev, ...built]
      })
      return built.length
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coerceCell, columns, makeEmptyRow, isRowFilled, setRows, headerLabels.join('|')],
  )

  useEffect(() => {
    if (!open) return
    const onDocPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      const text = e.clipboardData?.getData('text') ?? ''
      if (!text.trim()) return
      e.preventDefault()
      const added = appendRowsFromText(text)
      if (added > 0) snackbar.success(`Pasted ${added} new ${rowNoun}${added === 1 ? '' : 's'}`)
    }
    document.addEventListener('paste', onDocPaste)
    return () => document.removeEventListener('paste', onDocPaste)
  }, [open, appendRowsFromText, snackbar, rowNoun])

  const addRow = () => setRows((prev) => [...prev, makeEmptyRow()])

  const toggleSel = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })

  const toggleSelAll = () =>
    setSelected((prev) => (prev.size === rows.length && rows.length > 0 ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    setRows((prev) => {
      const next = prev.filter((_, i) => !selected.has(i))
      return next.length ? next : [makeEmptyRow()]
    })
    setSelected(new Set())
  }

  const deleteRow = (index: number) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [makeEmptyRow()]
    })
    setSelected((prev) => {
      const next = new Set<number>()
      prev.forEach((i) => {
        if (i === index) return
        next.add(i > index ? i - 1 : i)
      })
      return next
    })
  }

  const copyRows = async () => {
    const idx = selected.size > 0 ? [...selected].sort((a, b) => a - b) : rows.map((_, i) => i)
    const cellText = (i: number, c: MassColumn) => String(rows[i].data[c.key] ?? '').replace(/[\t\r\n]/g, ' ')
    const headerRow = columns.map((c) => c.label).join('\t')
    const tsv = [headerRow, ...idx.map((i) => columns.map((c) => cellText(i, c)).join('\t'))].join('\n')
    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const html =
      '<table><tr>' +
      columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('') +
      '</tr>' +
      idx.map((i) => '<tr>' + columns.map((c) => `<td>${escapeHtml(cellText(i, c))}</td>`).join('') + '</tr>').join('') +
      '</table>'

    let ok = false
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
      if (ClipboardItemCtor && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItemCtor({
            'text/plain': new Blob([tsv], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ])
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyViaSelection(html)
    if (!ok) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv)
          ok = true
        }
      } catch {
        ok = false
      }
    }
    if (!ok) ok = copyViaTextarea(tsv)
    if (ok) snackbar.success(`Copied ${idx.length} ${rowNoun}${idx.length === 1 ? '' : 's'} — paste into the grid or Excel.`)
    else snackbar.error('Browser blocked clipboard access. Open the page over HTTPS or localhost.')
  }

  const resetForm = () => {
    setRows([makeEmptyRow()])
    setSelected(new Set())
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: failCount > 0 ? 'error.main' : filledCount === 0 ? 'text.secondary' : 'success.main',
            }}
          >
            {failCount > 0
              ? `Cross-check: ${failCount} of ${rows.length} ${rowNoun}(s) need fixing`
              : filledCount === 0
                ? emptyStateText
                : `Cross-check passed — ${filledCount} ${rowNoun}(s) ready`}
          </Typography>
          <Tooltip
            title={`Dán từ Excel: copy rồi Ctrl+V để thêm dòng mới, hoặc dán trực tiếp vào ô.${
              showCopy ? ' Nút Copy sao chép dòng đã chọn.' : ''
            }`}
          >
            <ContentPasteIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={0.75}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={addRow}
            disabled={disabled}
            sx={toolbarBtnSx}
            title="Add row"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Add row</Box>
          </Button>
          {showCopy && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              onClick={() => void copyRows()}
              sx={toolbarBtnSx}
              title={selected.size > 0 ? `Copy (${selected.size})` : 'Copy all'}
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
                {selected.size > 0 ? `Copy (${selected.size})` : 'Copy all'}
              </Box>
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
            disabled={disabled || selected.size === 0}
            onClick={deleteSelected}
            sx={toolbarBtnSx}
            title={selected.size > 0 ? `Delete (${selected.size})` : 'Delete selected'}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
              {selected.size > 0 ? `Delete (${selected.size})` : 'Delete selected'}
            </Box>
          </Button>
          <Button size="small" variant="text" onClick={resetForm} disabled={disabled} sx={toolbarBtnSx} title="Reset">
            Reset
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ maxHeight: '48vh', overflow: 'auto', border: '1px solid #bdbdbd', bgcolor: '#fff' }}>
        <Table size="small" stickyHeader sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...massHeaderSx, width: 44 }} />
              <TableCell padding="checkbox" align="left" sx={{ bgcolor: '#fafafa', width: 44, pl: 0.75, pr: 2 }}>
                <Checkbox
                  size="small"
                  indeterminate={selected.size > 0 && !allSelected}
                  checked={allSelected}
                  onChange={toggleSelAll}
                  disabled={disabled}
                  sx={{ p: 0.25, ml: 0 }}
                />
              </TableCell>
              <TableCell sx={{ ...massHeaderSx, width: 36, textAlign: 'center', px: 0.25 }} title="Cross-check status">
                ✓
              </TableCell>
              {columns.map((c, i) => (
                <TableCell key={c.key} sx={{ ...massHeaderSx, minWidth: c.width, ...(i === 0 ? { pl: 1.25 } : {}) }}>
                  {c.label}
                  {c.required ? ' *' : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, index) => {
              const isSel = selected.has(index)
              const verdict = verdicts[index]
              return (
                <TableRow key={r.id} hover selected={isSel}>
                  <TableCell sx={{ ...massCellSx, width: 44, verticalAlign: 'middle' }}>
                    <Tooltip title="Delete row">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={disabled || rows.length === 1}
                          onClick={() => deleteRow(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell padding="checkbox" align="left" sx={{ width: 44, pl: 0.75, pr: 2 }}>
                    <Checkbox
                      size="small"
                      checked={isSel}
                      onChange={() => toggleSel(index)}
                      disabled={disabled}
                      sx={{ p: 0.25, ml: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...massCellSx, width: 36, textAlign: 'center', verticalAlign: 'middle', px: 0.25 }}>
                    {verdict?.ok ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: 17, color: 'success.main', verticalAlign: 'middle' }} />
                    ) : (
                      <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{verdict?.messages.join('\n')}</Box>}>
                        <ErrorOutlineIcon sx={{ fontSize: 17, color: 'error.main', verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  {columns.map((c, colIdx) => (
                    <TableCell
                      key={c.key}
                      sx={{ ...massCellSx, ...(colIdx === 0 ? { pl: 1.25 } : {}), bgcolor: cellBg(index, c.key) }}
                      onPasteCapture={(e) => handlePaste(index, colIdx, e)}
                    >
                      {c.kind === 'combo' ? (
                        <ComboCell
                          value={r.data[c.key] ?? ''}
                          options={c.options ?? []}
                          onChange={(v) => setCell(index, c.key, v)}
                          disabled={disabled}
                        />
                      ) : c.suggest ? (
                        <SuggestCell
                          value={r.data[c.key] ?? ''}
                          options={c.suggest}
                          numeric={c.kind === 'number'}
                          onChange={(v) => setCell(index, c.key, v)}
                          disabled={disabled}
                        />
                      ) : (
                        <TextField
                          value={c.kind === 'date' ? safeDateValue(r.data[c.key] ?? '') : (r.data[c.key] ?? '')}
                          onChange={(e) => setCell(index, c.key, e.target.value)}
                          disabled={disabled}
                          size="small"
                          fullWidth
                          type={c.kind === 'date' ? 'date' : 'text'}
                          InputLabelProps={c.kind === 'date' ? { shrink: true } : undefined}
                          inputProps={
                            c.kind === 'number' ? { style: { textAlign: 'right' }, inputMode: 'decimal' } : undefined
                          }
                          sx={massInputSx}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
          {hasSum && (
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    bgcolor: '#fafafa',
                    borderTop: (theme) => `2px solid ${theme.palette.divider}`,
                    py: 0.5,
                    pl: 1.25,
                    fontSize: '0.72rem',
                    position: 'sticky',
                    bottom: 0,
                  }}
                >
                  Total
                </TableCell>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.kind === 'number' ? 'right' : 'left'}
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      bgcolor: '#fafafa',
                      borderTop: (theme) => `2px solid ${theme.palette.divider}`,
                      py: 0.5,
                      px: 0.75,
                      fontSize: '0.72rem',
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      bottom: 0,
                    }}
                  >
                    {c.sum ? fmtSum(sumTotals[c.key] ?? 0) : ''}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Box>

      {failCount > 0 && (
        <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
            Cross-check issues — fix before Save
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {verdicts.flatMap((v, i) =>
              v.ok
                ? []
                : [
                    <Typography key={i} component="li" variant="caption" sx={{ display: 'list-item' }}>
                      {`Row ${i + 1}: ${v.messages.join(', ')}`}
                    </Typography>,
                  ],
            )}
          </Box>
        </Alert>
      )}
    </Stack>
  )
}
